import { useRef, useState } from "react";
import type { PnlChartSectionType, TimeRangeFinite } from "types/types";
import { ExtraPnlColumnsEnum } from "types/enums";
import { getIsSafari, useBreakpoints } from "constants/constants";
import { startCase, uniq } from "lodash";
import useContainerDimensions from "utils/useContainerDimensions";
import animateScrollTo from "animated-scroll-to";
import { SERVER_NAMES } from "components/Nav/Drawers/RightDrawer/FlightDebugger/SERVER_NAMES";
import Spinner from "components/SimpleComponents/Spinner/Spinner";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import ScrollContainer, { ScrollEvent } from "react-indiana-drag-scroll";
import {
  useIsSingleProcessPage,
  useVisibleProcessIds,
} from "utils/customHooks/genericHooks";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { CUSTOMER_UNITS_DISPLAY_NAMES_BY_MACHINE } from "constants/customers";
import {
  constructPnlChartPages,
  getDataGettersForColumns,
} from "./pnlChartColumnsUtils";
import { PnlColumnType } from "./pnlChartColumnsTypes";
import {
  PagesWrapperStyles,
  PnlSummaryChartColumnsStyles,
} from "./PnlChartColumnsStyles";
import { DotsOverflowStepper } from "./subcomponents/DotsOverflowStepper";
import { PnlColumn } from "./subcomponents/PnlColumn";
import {
  EXTRA_PNL_COLUMNS,
  PNL_CHART_COLUMNS,
  TAG_NAMES_COLUMN,
} from "./columnDefinitions";
import { useIsDarkMode } from "store/atoms";
import { useIsMidgardDDAEnabled } from "components/Dashboard/GroupMetrics/useMetrics";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";
import { useMediaQuery } from "@mui/material";
import { useIsTakingSnapshot } from "pages/SnapshotsPage/SnapshotElementButton";
import { useShouldFetchPnlChartLossesByUnit } from "./data-getters/PnlChartLossCountGetter";

// tslint:disable-next-line: no-big-function
export default function PnlChartColumns({
  pnlSectionsDisplay,
  isInsideMachineBreakdownChart = false,
  isLoading = false,
  isSubordinateProcessesOnly = false,
  timeRange,
  disableAnimation = false,
}: {
  pnlSectionsDisplay: PnlChartSectionType[];
  isInsideMachineBreakdownChart?: boolean;
  isLoading?: boolean;
  isSubordinateProcessesOnly?: boolean;
  timeRange: TimeRangeFinite | null;
  disableAnimation?: boolean;
}) {
  const isStorybook = useIsStorybook();
  const isPrint = useMediaQuery("print");
  const [isTakingSnapshot] = useIsTakingSnapshot();
  const isFromSnapshotsPage =
    window.location.pathname.includes("fromSnapshots");
  const disablePagination = isPrint || isFromSnapshotsPage || isTakingSnapshot;
  const [isShowMoreExpanded, setIsShowMoreExpanded] =
    useState(disablePagination);
  const toggleShowMore = () => setIsShowMoreExpanded(!isShowMoreExpanded);

  // random key (16 digits no decimals) to identify this pnl chart, on pages with multiple charts
  const pnlChartKey = useRef(
    isStorybook ? 1234567890123456 : Math.round(Math.random() * 10 ** 16)
  ).current;

  const { pnlChartColumns } = usePnlChartColumns();

  const { isTabletOrLarger } = useBreakpoints();
  const breakpointKey = isTabletOrLarger ? "md" : "sm";
  const [ref, dimensions] = useContainerDimensions();
  const pnlChartWidth = dimensions.width || 0;
  const firstColFixedWidth = TAG_NAMES_COLUMN.minWidthsPx[breakpointKey]; // first column has constant width

  /** width of one horizontal, scrollable "page" */
  const pageWidth = pnlChartWidth - firstColFixedWidth;
  /** a "page" is an array of columns that fits to the right of the fixed first column,
   *  in the visible horizontal area of the screen width, according to its minWidthsPx
   */
  const pnlChartPages = constructPnlChartPages(
    pnlChartColumns,
    breakpointKey,
    pageWidth,
    disablePagination
  );
  const totalScrollableColumnsWidth = pnlChartColumns.reduce(
    (acc, cur) => acc + cur.minWidthsPx[breakpointKey],
    0
  );
  // if it overflows horizontally, show the hz pages + scrollbar, stepper buttons, and set each column to its flexGrow
  // else show a single page, and set each column to its minWidth + flexGrow
  const doesOverflowHz =
    totalScrollableColumnsWidth > pageWidth &&
    !(isPrint || isFromSnapshotsPage);

  const {
    scrollContainerRef,
    activeStep,
    isAnimating,
    setIsAnimating,
    handleStepperClick,
    handleScrollHz,
    handleScrollEnd,
  } = useHorizontalScrollHandlers(pageWidth);

  const ScrollContainerStorybookFriendly =
    useScrollContainerStorybookFriendly();

  const isMidgardDDAEnabled = useIsMidgardDDAEnabled();

  const isSingleProcessPage = useIsSingleProcessPage();
  const [isDarkMode] = useIsDarkMode();
  const [
    {
      general: { extraPnlChartColumns },
    },
  ] = useFlightConfig();
  const hasExtraColumns = extraPnlChartColumns.length > 0;
  return (
    <PnlSummaryChartColumnsStyles
      ref={ref}
      className={`PnlChart pnlChartKey-${pnlChartKey} ${
        hasExtraColumns ? "with-extra-columns" : ""
      }`}
      data-component-name={"PnL Chart"}
      data-server-names={[
        isMidgardDDAEnabled
          ? isSingleProcessPage || isInsideMachineBreakdownChart
            ? SERVER_NAMES.midgardDataDumpsFullRangeByProcess
            : isSubordinateProcessesOnly
              ? SERVER_NAMES.midgardDataDumpFullRangeSubordinateProcesses
              : SERVER_NAMES.midgardDataDumpFullRange
          : SERVER_NAMES.askrAggregates,
      ]}
      $doesOverflowHz={doesOverflowHz}
      $isDarkMode={isDarkMode}
      $isTakingSnapshot={isTakingSnapshot}
    >
      {!isLoading && doesOverflowHz && !isTakingSnapshot && (
        <DotsOverflowStepper
          {...{
            activeStep,
            handleStepperClick,
            numSteps: pnlChartPages.length,
          }}
        />
      )}
      {isLoading ? (
        <div className="spinnerWrapper">
          <Spinner width={32} />
        </div>
      ) : null}
      {/* first column is fixed */}
      {[TAG_NAMES_COLUMN].map((pnlColumn, columnIdx) => {
        return (
          <PnlColumn
            key={columnIdx}
            {...{
              pnlColumn,
              pnlSectionsDisplay,
              columnIdx,
              pnlChartWidth,
              isShowMoreExpanded,
              toggleShowMore,
              pnlChartKey,
              style: {
                width: pnlColumn.minWidthsPx[breakpointKey],
                flexShrink: 0,
              },
              timeRange,
              isLoading,
              isLeftmostColumnInChart: true,
              isRightmostColumnInChart: false,
              disableAnimation,
            }}
          />
        );
      })}
      {/* following columns are scrollable if they overflow */}
      <PagesWrapperStyles
        {...{
          $isLoading: isLoading,
          $pageWidth: pageWidth,
          $isAnimating: isAnimating,
          $doesOverflowHz: doesOverflowHz,
        }}
      >
        <ScrollContainerStorybookFriendly
          innerRef={scrollContainerRef}
          onScroll={(e: ScrollEvent) =>
            scrollContainerRef
              ? handleScrollHz(scrollContainerRef?.current.scrollLeft)
              : null
          }
          onStartScroll={() => setIsAnimating(true)}
          onEndScroll={(e: ScrollEvent) =>
            scrollContainerRef
              ? handleScrollEnd(scrollContainerRef?.current.scrollLeft)
              : null
          }
          className="scroll-container"
          horizontal={true}
          vertical={false}
          hideScrollbars={false}
        >
          {pnlChartPages.map((columnsInPage, pageIdx) => {
            return (
              <div key={pageIdx} className={`page page-${pageIdx + 1}`}>
                {columnsInPage.map((pnlColumn, columnIdxInPage) => {
                  const prevPages = pnlChartPages.slice(0, pageIdx);

                  const columnIdxInAllPages =
                    columnIdxInPage +
                    prevPages.reduce(
                      (accCols, pgOfCols) => accCols + pgOfCols.length,
                      0
                    );

                  const isRightmostColumnInChart =
                    columnIdxInAllPages === pnlChartColumns.length - 1;

                  const isRightmostColumnInPage =
                    columnIdxInPage === columnsInPage.length - 1;

                  return (
                    <PnlColumn
                      key={columnIdxInAllPages}
                      {...{
                        pnlColumn: {
                          ...pnlColumn,
                          flexGrow:
                            // stretch the column to full width if it's the only one in the page
                            columnsInPage.length === 1
                              ? { sm: 1, md: 1 }
                              : pnlColumn.flexGrow,
                        },
                        isLoading,
                        pnlSectionsDisplay,
                        pnlChartWidth,
                        isShowMoreExpanded,
                        toggleShowMore,
                        isRightmostColumnInChart,
                        isRightmostColumnInPage,
                        columnIdx: columnIdxInAllPages,
                        style: {
                          minWidth: pnlColumn.minWidthsPx[breakpointKey],
                        },
                        pnlChartKey,
                        timeRange,
                        disableAnimation,
                      }}
                    />
                  );
                })}
              </div>
            );
          })}
        </ScrollContainerStorybookFriendly>
      </PagesWrapperStyles>
    </PnlSummaryChartColumnsStyles>
  );
}

/** handlers for horizontal drag-to-scroll,
 * and for clicking the DotsOverflowStepper to animate between pages
 */
function useHorizontalScrollHandlers(pageWidth: number) {
  const isStorybook = useIsStorybook();
  const isStoryshots = isStorybook && process.env.NODE_ENV === "test";
  const isSafari = getIsSafari();

  // to animate the scrollLeft on stepper click
  const [activeStep, setActiveStep] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const scrollContainerRef = useRef(null as any);

  /** animate the scrollLeft to the appropriate page associated with the clicked stepper dot button */
  function animateHzScrollToStep(newActiveStep: number) {
    if (isStoryshots) {
      /* in storybook snapshots, the stepper seems to auto-switch between steps - so, don't animate in storybook */
      return;
    }
    setActiveStep(newActiveStep);
    const finalScrollLeft = pageWidth * newActiveStep;

    // animateScrollTo doesn't work with safari
    if (isSafari) {
      scrollContainerRef.current.scrollLeft = finalScrollLeft;
      return;
    }

    setIsAnimating(true); // disable css scroll-snap while animating
    animateScrollTo([finalScrollLeft, null], {
      elementToScroll: scrollContainerRef.current,
      // Maximum duration of the scroll animation
      maxDuration: 3000,
      // Minimum duration of the scroll animation
      minDuration: 250,
      // Duration of the scroll per 1000px
      speed: 500,
      // Animation easing function, with "easeOutCubic" as default
      easing: t => --t * t * t + 1,
    }).then(() => {
      setIsAnimating(false); // re-enable css scroll-snap
    });
  }

  /** when we finish scrolling, update the active step to the one nearest to our current scroll position,
   * then animate the rest of the way there
   */
  function handleScrollEnd(scrollLeft: number) {
    const newActiveStep = Math.round(scrollLeft / pageWidth);
    animateHzScrollToStep(newActiveStep);
  }

  /** when we click a stepper dot, animate the hz scroll position */
  function handleStepperClick(newActiveStep: number) {
    animateHzScrollToStep(newActiveStep);
  }

  /** when we scroll horizontally, update the DotsOverflowStepper page-indicator */
  function handleScrollHz(scrollLeft: number) {
    const hasArrivedAtNewPage = scrollLeft % pageWidth === 0;
    if (hasArrivedAtNewPage) {
      const newActiveStep = scrollLeft / pageWidth;
      setActiveStep(newActiveStep);
    }
  }

  return {
    activeStep,
    isAnimating,
    setIsAnimating,
    handleStepperClick,
    handleScrollHz,
    handleScrollEnd,
    scrollContainerRef: isStoryshots ? null : scrollContainerRef,
  };
}

/** default PnL columns + customer-specific extra columns */
function usePnlChartColumns(): {
  pnlChartColumns: PnlColumnType[];
  dataGetters: (({
    timeRange,
  }: {
    timeRange: TimeRangeFinite | null;
  }) => null)[];
} {
  const ckey = useCustomerKey();
  const visibleProcessIds = useVisibleProcessIds();
  const [
    {
      general: { extraPnlChartColumns },
    },
  ] = useFlightConfig();

  const extraPnlColumns = extraPnlChartColumns.map(
    key => EXTRA_PNL_COLUMNS[key]
  );

  // for the outputLoss column, split it into multiple columns if necessary,
  // i.e. one column per unit (e.g. cups, bottles)
  const extraPnlColumnsSplitByUnit = extraPnlColumns.reduce((accCols, cur) => {
    const shouldSplitOutputLossColumn =
      cur.dataKey === ExtraPnlColumnsEnum.outputLoss &&
      ckey in CUSTOMER_UNITS_DISPLAY_NAMES_BY_MACHINE;

    if (shouldSplitOutputLossColumn) {
      const uniqueUnits = uniq(
        Object.entries(CUSTOMER_UNITS_DISPLAY_NAMES_BY_MACHINE[ckey])
          .filter(([processId, unit]) => visibleProcessIds.includes(processId))
          .map(([processId, unit]) => unit)
      );
      const oneColumnPerUnit: PnlColumnType[] = uniqueUnits.map(unit => ({
        ...cur,
        outputLossUnit: unit, // to grab data from the dataDumpAggregate
        columnHeaderTitle: startCase(unit), // replaces default title "Units"
      }));

      return [...accCols, ...oneColumnPerUnit];
    } else {
      return [...accCols, cur];
    }
  }, [] as PnlColumnType[]);
  const pnlChartColumns = [
    ...PNL_CHART_COLUMNS,
    ...extraPnlColumnsSplitByUnit,
  ].sort((a, b) => a.order - b.order);

  const dataGetters = getDataGettersForColumns(pnlChartColumns);

  return {
    pnlChartColumns,
    dataGetters,
  };
}

/** fetch any custom data needed by specific pnl chart columns */
export function PnlChartDataGetters({
  timeRange,
}: {
  timeRange: TimeRangeFinite | null;
}) {
  const { dataGetters } = usePnlChartColumns();
  const shouldFetchPnlChartLossesByUnit = useShouldFetchPnlChartLossesByUnit();
  return shouldFetchPnlChartLossesByUnit ? (
    <>
      {dataGetters.map((DataGetter, idx) => (
        <DataGetter key={idx} timeRange={timeRange} />
      ))}
    </>
  ) : null;
}

/** ScrollContainer animation throws an error during  Storybook snapshots -- so in Storybook, use a div instead */
function useScrollContainerStorybookFriendly() {
  const isStorybook = useIsStorybook(); // for working storyshots
  const isStoryshots = isStorybook && process.env.NODE_ENV === "test";
  return (
    isStoryshots
      ? ({ children, onStartScroll, onScroll, className, ...rest }) => (
          <div
            {...{ className }}
            onScroll={e => {
              onStartScroll();
              onScroll(e.currentTarget.scrollLeft);
            }}
          >
            {children}
          </div>
        )
      : ScrollContainer
  ) as any;
}
